import { emptyWorkspace, parseWorkspace } from '@pebblecode/codegen';
import {
  CodeLanguage,
  type ExerciseCategory,
  type ExerciseListResponse,
  type ProjectDto,
  type ProjectSummary,
} from '@pebblecode/shared';
import { useEffect, useMemo, useState, type FormEvent } from 'react';

import { api } from '../api/client.js';
import { navigate, onLinkClick, useTitle } from '../app/router.js';
import { Icon } from '../components/Icon.js';
import { AccountMenu } from '../components/AccountMenu.js';
import { Brand, LangSwitch, ThemeToggle } from '../components/TopBar.js';
import { ClassesCard, HomeworkCard, LiveNowCard } from '../homework/HomeCards.js';
import { ProgressCard } from '../rewards/ProgressCard.js';
import { RewardsChip } from '../rewards/RewardsUi.js';
import { previewLines } from '../editor/blockPreview.js';
import { Difficulty } from '../editor/ExercisePanel.js';
import { timeAgo, useT } from '../i18n/index.js';
import { codeLangPrefStore, useUiLang } from '../state/prefs.js';
import { takeClaimed } from '../state/auth.js';
import { useStore } from '../state/store.js';
import { AccountBanners } from './AccountBanners.js';

type Filter = 'all' | ExerciseCategory;
const COLLAPSED = 6;

export const SESSION_CODE = /^[A-Z0-9]{6}$/;

function useHomeData() {
  const lang = useUiLang();
  const [catalog, setCatalog] = useState<ExerciseListResponse | null>(null);
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [latest, setLatest] = useState<ProjectDto | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .exercises(lang)
      .then((c) => live && setCatalog(c))
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, [lang]);

  useEffect(() => {
    let live = true;
    api
      .projects()
      .then(async (list) => {
        if (!live) return;
        setProjects(list);
        const first = list[0];
        if (first) {
          const full = await api.project(first.id);
          if (live) setLatest(full);
        }
      })
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, []);

  return { catalog, projects, setProjects, latest, error };
}

export function HomePage() {
  const t = useT();
  const lang = useUiLang();
  const { catalog, projects, setProjects, latest, error } = useHomeData();
  const codePref = useStore(codeLangPrefStore);
  useTitle(null);
  const [filter, setFilter] = useState<Filter | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(false);

  const byExercise = useMemo(() => {
    const m = new Map<string, ProjectSummary>();
    for (const p of projects ?? []) {
      if (!p.exerciseSlug) continue;
      const prev = m.get(p.exerciseSlug);
      // "Done" wins over "in progress" when there are several projects for one exercise.
      if (!prev || (p.solved && !prev.solved)) m.set(p.exerciseSlug, p);
    }
    return m;
  }, [projects]);

  const latestExercise = latest?.exerciseSlug
    ? catalog?.exercises.find((e) => e.slug === latest.exerciseSlug)
    : undefined;
  // Default filter: the category of the exercise you were last working on.
  const activeFilter: Filter = filter ?? latestExercise?.category ?? 'all';
  const shown = (catalog?.exercises ?? []).filter(
    (e) => activeFilter === 'all' || e.category === activeFilter,
  );
  const visible = expanded ? shown : shown.slice(0, COLLAPSED);
  const done = shown.filter((e) => byExercise.get(e.slug)?.solved).length;
  const freeProjects = (projects ?? []).filter((p) => !p.exerciseSlug);

  const preview = useMemo(
    () => (latest ? previewLines(parseWorkspace(latest.workspace), lang) : []),
    [latest, lang],
  );

  const startOver = async () => {
    if (!latest || !window.confirm(t.home.startOverConfirm)) return;
    const exercise = latest.exerciseSlug ? await api.exercise(latest.exerciseSlug, lang) : null;
    await api.updateProject(latest.id, {
      rev: latest.rev,
      workspace: exercise?.starter ?? emptyWorkspace(),
    });
    navigate(`/p/${latest.id}`);
  };

  const join = (e: FormEvent) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (!SESSION_CODE.test(c)) {
      setCodeError(true);
      return;
    }
    navigate(`/s/${c}`);
  };

  const remove = async (p: ProjectSummary) => {
    if (!window.confirm(t.home.deleteConfirm(p.title))) return;
    await api.deleteProject(p.id);
    setProjects((list) => list?.filter((x) => x.id !== p.id) ?? null);
  };

  const lastRunText = (p: ProjectDto) => {
    const s = p.lastRun?.status;
    if (!s) return null;
    return {
      text:
        s === 'finished'
          ? t.home.lastRun.finished
          : s === 'step-limit'
            ? t.home.lastRun.stepLimit
            : s === 'error'
              ? t.home.lastRun.error
              : t.home.lastRun.stopped,
      tone: s === 'finished' ? 'ok' : s === 'stopped' ? 'muted' : 'accent',
    };
  };

  const title = (p: { exerciseSlug: string | null; title: string }) =>
    (p.exerciseSlug && catalog?.exercises.find((e) => e.slug === p.exerciseSlug)?.title) || p.title;

  return (
    <div className="home">
      <header className="topbar home-topbar">
        <Brand />
        <div className="topbar-spacer" />
        <nav className="home-nav">
          <a href="#exercises" aria-current="page">
            {t.nav.exercises}
          </a>
          <a href="#projects">{t.nav.myProjects}</a>
          <a href="#join">{t.nav.joinSession}</a>
        </nav>
        <RewardsChip />
        <AccountMenu />
        <LangSwitch />
        <ThemeToggle />
      </header>

      <main className="home-main">
        <div className="home-left">
          <AccountBanners />
          <ClaimedNotice />
          <LiveNowCard />
          <HomeworkCard />
          {latest ? (
            <section className="card continue-card" aria-labelledby="continue-title">
              <div className="continue-text">
                <span className="eyebrow">{t.home.pickUp}</span>
                <h1 id="continue-title">{title(latest)}</h1>
                <div className="continue-meta">
                  {latestExercise && (
                    <>
                      <span>
                        {t.category[latestExercise.category]} ·{' '}
                        {t.exercise
                          .position(latestExercise.position, latestExercise.total)
                          .toLowerCase()}
                      </span>
                      <i className="meta-dot" />
                    </>
                  )}
                  <span>{t.home.editedAgo(timeAgo(latest.updatedAt, lang))}</span>
                  {lastRunText(latest) && (
                    <>
                      <i className="meta-dot" />
                      <span className={`meta-run tone-${lastRunText(latest)!.tone}`}>
                        <i className="status-dot" />
                        {lastRunText(latest)!.text}
                      </span>
                    </>
                  )}
                </div>
                <div className="continue-actions">
                  <a
                    href={`/p/${latest.id}`}
                    onClick={onLinkClick}
                    className="btn btn-primary btn-lg"
                  >
                    <Icon name="play" size={14} />
                    {t.home.continue}
                  </a>
                  <button className="btn btn-lg" onClick={() => void startOver()}>
                    {t.home.startOver}
                  </button>
                </div>
              </div>
              <div className="mini-blocks" aria-hidden="true">
                {preview.map((l, i) => (
                  <span
                    key={i}
                    className={`mini-block cat-${l.category}${i === 0 ? ' mini-hat' : ''}`}
                    style={{ marginLeft: l.depth * 14 }}
                  >
                    {l.text}
                  </span>
                ))}
              </div>
            </section>
          ) : (
            projects && (
              <section className="card continue-card">
                <div className="continue-text">
                  <h1>{t.home.welcomeTitle}</h1>
                  <p className="welcome-text">{t.home.welcomeText}</p>
                  {catalog?.exercises[0] && (
                    <div className="continue-actions">
                      <a
                        href={`/e/${catalog.exercises[0].slug}`}
                        onClick={onLinkClick}
                        className="btn btn-primary btn-lg"
                      >
                        <Icon name="play" size={14} />
                        {t.home.start}
                      </a>
                    </div>
                  )}
                </div>
              </section>
            )
          )}

          <section id="exercises" className="catalog" aria-labelledby="catalog-title">
            <div className="catalog-head">
              <h2 id="catalog-title">{t.home.exercises}</h2>
              <div className="topbar-spacer" />
              <div className="filter-tabs" role="tablist" aria-label={t.home.exercises}>
                {(['all', ...(catalog?.categories ?? [])] as Filter[]).map((f) => (
                  <button
                    key={f}
                    role="tab"
                    aria-selected={f === activeFilter}
                    onClick={() => {
                      setFilter(f);
                      setExpanded(false);
                    }}
                  >
                    {f === 'all' ? t.category.all : t.category[f]}
                  </button>
                ))}
              </div>
            </div>
            {error && <p className="muted-text">{t.common.somethingWrong}</p>}
            <div className="exercise-grid">
              {visible.map((e) => {
                const p = byExercise.get(e.slug);
                const state = p?.solved ? 'done' : p ? 'progress' : null;
                return (
                  <a
                    key={e.slug}
                    href={`/e/${e.slug}`}
                    onClick={onLinkClick}
                    className={`card exercise-card${state === 'progress' ? ' in-progress' : ''}`}
                  >
                    <div className="exercise-card-top">
                      <span className="exercise-num">{String(e.position).padStart(2, '0')}</span>
                      <Difficulty
                        level={e.difficulty}
                        label={t.exercise.difficulty(e.difficulty)}
                      />
                      <div className="topbar-spacer" />
                      {state === 'done' && (
                        <span className="badge-done">
                          <Icon name="check" size={13} strokeWidth={2.6} />
                          {t.exercise.done}
                        </span>
                      )}
                      {state === 'progress' && (
                        <span className="badge-progress">{t.exercise.inProgress}</span>
                      )}
                    </div>
                    <span className="exercise-card-title">{e.title}</span>
                    <span className="exercise-card-summary">{e.summary}</span>
                  </a>
                );
              })}
            </div>
            {catalog && (
              <div className="catalog-foot">
                {shown.length > COLLAPSED && !expanded && (
                  <button className="link-btn" onClick={() => setExpanded(true)}>
                    {t.home.moreIn(
                      shown.length - COLLAPSED,
                      activeFilter === 'all' ? t.category.all : t.category[activeFilter],
                    )}
                  </button>
                )}
                {shown.length > COLLAPSED && !expanded && <span> · </span>}
                <span>{t.home.progress(done, shown.length)}</span>
              </div>
            )}
          </section>
        </div>

        <aside className="home-right">
          <section id="join" className="join-card" aria-labelledby="join-title">
            <h2 id="join-title">{t.home.joinTitle}</h2>
            <p>{t.home.joinText}</p>
            <form onSubmit={join}>
              <input
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase().slice(0, 6));
                  setCodeError(false);
                }}
                placeholder={t.home.joinPlaceholder}
                aria-label={t.home.joinTitle}
                aria-invalid={codeError}
                autoComplete="off"
                spellCheck={false}
              />
              <button className="btn btn-primary join-btn" type="submit">
                {t.home.join}
              </button>
            </form>
            {codeError && <p className="join-error">{t.home.joinInvalid}</p>}
          </section>

          <ClassesCard />

          <ProgressCard />

          <section className="card side-card" aria-labelledby="langs-title">
            <h2 id="langs-title">{t.home.codeShownAs}</h2>
            <p>{t.home.codeShownAsText}</p>
            <div className="lang-grid">
              {CodeLanguage.options.map((l) => (
                <button
                  key={l}
                  aria-pressed={(codePref || (lang === 'ro' ? 'cpp' : 'js')) === l}
                  onClick={() => codeLangPrefStore.set(l)}
                >
                  {t.code.tabs[l]}
                </button>
              ))}
            </div>
          </section>

          <section id="projects" className="card side-card" aria-labelledby="projects-title">
            <div className="side-card-head">
              <h2 id="projects-title">{t.home.myProjects}</h2>
              <a href="/new" onClick={onLinkClick} className="btn btn-sm new-btn">
                <Icon name="plus" size={13} strokeWidth={2.6} />
                {t.home.newProject}
              </a>
            </div>
            {freeProjects.length === 0 ? (
              <p>{t.home.noProjects}</p>
            ) : (
              <ul className="project-list">
                {freeProjects.map((p, i) => (
                  <li key={p.id}>
                    <a href={`/p/${p.id}`} onClick={onLinkClick}>
                      <i className={`project-dot dot-${i % 4}`} />
                      <span className="project-title">{p.title}</span>
                      <span className="project-time">{timeAgo(p.updatedAt, lang)}</span>
                    </a>
                    <button
                      className="icon-btn project-delete"
                      aria-label={`${t.home.delete}: ${p.title}`}
                      title={t.home.delete}
                      onClick={() => void remove(p)}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </main>
    </div>
  );
}

/** Once, after login: what moved from this browser into the account. */
function ClaimedNotice() {
  const t = useT();
  const [claimed, setClaimed] = useState(() => takeClaimed());
  if (!claimed) return null;
  return (
    <div className="info-toast" role="status">
      <Icon name="check" size={16} strokeWidth={2.6} />
      <span>{t.auth.claimed(claimed.projects, claimed.points)}</span>
      <button className="icon-btn" aria-label={t.common.close} onClick={() => setClaimed(null)}>
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}
